import React, { useState, useEffect } from 'react';
import { Text } from '../ui';

interface BiasTestProps {
  test: {
    questions: {
      question: string;
      options: string[];
      correctOption: number;
      explanation: string;
    }[];
  };
  variant?: 'card' | 'transparent';
  onAnswerSubmit?: (isCorrect: boolean, selectedOptionIndex?: number, questionIndex?: number) => void;
  answeredQuestions?: Record<number, number>;
  questionIndex?: number;
}

export const BiasTest: React.FC<BiasTestProps> = ({ 
  test, 
  variant = 'card', 
  onAnswerSubmit,
  answeredQuestions: externalAnsweredQuestions,
  questionIndex = 0
}) => {
  const [internalAnsweredQuestions, setInternalAnsweredQuestions] = useState<Record<number, number>>({});
  
  // Use external state if provided, otherwise use internal state
  const answeredQuestions = externalAnsweredQuestions !== undefined ? 
    externalAnsweredQuestions : internalAnsweredQuestions;

  // Force re-render when question index changes
  const [currentQuestion] = useState(test.questions[0]);
  
  // Update current question when test or questionIndex changes
  useEffect(() => {
    // This effect ensures the component responds to question changes
  }, [test, questionIndex]);

  const optionButtonClasses = (isSelected: boolean) => `
    w-full p-4 rounded-lg text-left transition-colors break-words overflow-hidden mx-auto my-1
    ${isSelected 
      ? 'border-2 border-orange-500 bg-orange-50 shadow-md'
      : 'border border-gray-200 hover:border-orange-300 hover:shadow-md'
    }
  `;

  const questionCardClasses = variant === 'card' 
    ? 'bg-white rounded-lg p-6 shadow-lg' 
    : '';

  const handleAnswerClick = (optionIndex: number) => {
    if (answeredQuestions[questionIndex] === undefined) {
      const isCorrect = optionIndex === test.questions[0].correctOption;
      
      if (externalAnsweredQuestions === undefined) {
        setInternalAnsweredQuestions({ ...internalAnsweredQuestions, [questionIndex]: optionIndex });
      }
      
      onAnswerSubmit?.(isCorrect, optionIndex, questionIndex);
    }
  };

  return (
    <div className="space-y-6 w-full overflow-visible px-1">
      {test.questions.map((q, idx) => (
        <div key={`question-${questionIndex}-${idx}`} className={`${questionCardClasses} mb-8 last:mb-0 overflow-visible`}>
          <Text className="font-semibold text-gray-800 mb-6 break-words w-full">{q.question}</Text>
          <div className="space-y-4 w-full overflow-visible">
            {q.options.map((option, optIdx) => {
              const isAnswered = answeredQuestions[questionIndex] !== undefined;
              const isSelected = answeredQuestions[questionIndex] === optIdx;
              const isCorrect = optIdx === q.correctOption;
              
              return (
                <button
                  key={`option-${questionIndex}-${optIdx}`}
                  onClick={() => handleAnswerClick(optIdx)}
                  disabled={isAnswered}
                  className={optionButtonClasses(isSelected)}
                >
                  <div className="flex items-center w-full overflow-hidden">
                    <div className="flex-1 pr-3 break-words overflow-hidden">{option}</div>
                    {isAnswered && isCorrect && (
                      <span className="text-green-600 flex-shrink-0 ml-2">✓</span>
                    )}
                    {isAnswered && isSelected && !isCorrect && (
                      <span className="text-red-600 flex-shrink-0 ml-2">✗</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {answeredQuestions[questionIndex] !== undefined && (
            <Text className={`mt-6 text-sm break-words overflow-hidden ${
              answeredQuestions[questionIndex] === q.correctOption 
                ? 'text-green-600' 
                : 'text-red-600'
            }`}>
              {q.explanation}
            </Text>
          )}
        </div>
      ))}
    </div>
  );
}; 